import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

/**
 * Wraps vue-js-modal plugin
 * @see https://github.com/euvl/vue-js-modal
 */
export const dkModalAware = {
    props: {
        closeModal: {
            type: Function,
            default: () => {},
        },
        hideModalWithResponse: {
            type: Function,
            default: () => {},
        },
    },
    computed: {
        modalsCount() {
            return this.getModalElms().length;
        },
    },
    methods: {
        /**
         * Returns the modal body HTMLElement
         *
         * @returns {HTMLElement}
         */
        getModalBodyElm() {
            return document.querySelector('.modal__body');
        },
        /**
         * Returns all opened modals
         */
        getModalElms() {
            return document.querySelectorAll('[data-modal]');
        },
        /**
         * Wraps the vue-js-modal `show` method and opens a modal with a specific
         * name or component,and an optional configuration object.
         *
         * @see {@link https://github.com/euvl/vue-js-modal}
         * @param {String|Object} nameOrComponent - component definition
         * @param {Object} props - props passed to the component
         * @param {Object} opts - vue-js-modal options
         * @param {Object} events - vue-js-modal event listeners
         * @param {Object} config - custom configuration
         * @param {boolean} [config.disableBodyScroll=true] - disables iOS body scroll
         * @returns {Promise}
         */
        showModal(
            nameOrComponent = '',
            props = {},
            opts = {},
            events = {},
            config = {
                disableBodyScroll: true,
            }
        ) {
            // The modal name is mandatory.
            if (!opts.name) {
                throw new Error('The name of the modal is required.');
            }

            // Check whether or not the name of the modal is already taken.
            const modalElms = this.getModalElms();

            for (const modalElm of modalElms) {
                if (modalElm.getAttribute('data-modal') === opts.name) {
                    throw new Error('The name of the modal is already used.');
                }
            }

            const options = {
                ...opts,
                classes: ['modal', ...(config.disableBodyScroll ? ['modal--disabled-body-scroll'] : []), 'v--modal'],
                adaptive: true,
            };

            if (opts.classes) {
                if (Array.isArray(opts.classes)) {
                    options.classes = options.classes.concat(opts.classes);
                } else {
                    options.classes.push(opts.classes);
                }
            }

            // Please, read the comment in modalOpened regarding this const.
            const viewportWidth = !this.isWindows() ? document.documentElement.offsetWidth : null;

            return new Promise((resolve, reject) => {
                this.$modal.show(
                    nameOrComponent,
                    {
                        ...props,
                        closeModal(error) {
                            reject(error);
                            this.hideModal(opts.name);
                        },
                        hideModalWithResponse(response) {
                            resolve(response);
                            this.hideModal(opts.name);
                        },
                    },
                    options,
                    {
                        ...events,
                        opened: (e) => this.modalOpened(e, events, config, viewportWidth),
                        closed: (e) => this.modalClosed(e, events, config),
                    }
                );
            });
        },
        /**
         * When the modal opens, it appends to the html DOM element
         * a class and fires specific callback function when exists
         *
         * @param {Object} e
         * @param {Object} events
         * @param {Object} config
         */
        modalOpened(e, events = {}, config = {}, viewportWidth) {
            if (this.modalsCount === 1) {
                document.documentElement.classList.add('modal-open');
                const viewportWidthWithModal = document.documentElement.offsetWidth;

                /* 
                / Some users have an always-on scroll on their MacOS devices,
                / causing the page content to move to the right when the modal is opened,
                / because html overflow is set to hidden.
                / In order to avoid it, we need to detect the presence of the always-on scroll
                / by calculating the difference of the offsetWidth before and after the modal is opened
                / and to handle the right position of the elements accordingly.
                */
                const hasAlwaysOnScrollOnMac =
                    !this.isWindows() && viewportWidth && viewportWidthWithModal - viewportWidth === 15;

                if (this.isWindows() || hasAlwaysOnScrollOnMac) {
                    document.documentElement.classList.add('modal-open__padding');

                    const clickToCallSnackbar = document.querySelector('.click-to-call-snackbar');

                    if (clickToCallSnackbar && clickToCallSnackbar.classList.contains('tw-fixed')) {
                        clickToCallSnackbar.style.right = `calc(2rem + ${viewportWidthWithModal - viewportWidth}px)`;
                    }
                }
            }

            if (config.disableBodyScroll) {
                const modalBodyElm = this.getModalBodyElm();

                modalBodyElm && disableBodyScroll(modalBodyElm);
            }

            if (events.opened && typeof events.opened === 'function') {
                events.opened(e);
            }
        },
        /**
         * When the modal closes, it removes from the html DOM element
         * a class and fires specific callback function when exists
         *
         * @param {Object} e
         * @param {Object} events
         * @param {Object} config
         */
        modalClosed(e, events = {}, config = {}) {
            if (this.modalsCount === 1) {
                const htmlElm = document.documentElement;

                if (htmlElm.classList.contains('modal-open')) {
                    htmlElm.classList.remove('modal-open');
                }

                if (htmlElm.classList.contains('modal-open__padding')) {
                    htmlElm.classList.remove('modal-open__padding');

                    const clickToCallSnackbar = document.querySelector('.click-to-call-snackbar');

                    if (clickToCallSnackbar) {
                        clickToCallSnackbar.style.right = null;
                    }
                }
            }

            if (config.disableBodyScroll) {
                const modalBodyElm = this.getModalBodyElm();

                modalBodyElm && enableBodyScroll(modalBodyElm);
            }

            if (events.closed && typeof events.closed === 'function') {
                events.closed(e);
            }
        },
        /**
         * Close the modal.
         *
         * @param {String} name
         */
        hideModal(name = '') {
            if (name) {
                this.$modal.hide(name);
            } else {
                this.$emit('close');
            }
        },
        isWindows() {
            return /windows/.test(navigator.userAgent);
        },
    },
};
